import {
    Box, Typography, Grid, Paper, Button,
    Table, TableBody, TableCell, TableHead,
    TableRow, TableContainer, Chip, Avatar
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckIcon from "@mui/icons-material/Check";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { useEffect, useState } from "react";
import { investorApi } from "../../api/api";
import { useSelector } from "react-redux";

/* ── Animated Counter ── */
const AnimatedCounter = ({ target = 0 }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!target) return;
        let current = 0;
        const step = Math.max(1, Math.ceil(target / 60));
        const timer = setInterval(() => {
            current += step;
            if (current >= target) { setCount(target); clearInterval(timer); }
            else setCount(current);
        }, 18);
        return () => clearInterval(timer);
    }, [target]);
    return <>{count}</>;
};

/* ── How-it-works step ── */
const StepItem = ({ num, text, delay }) => (
    <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 2,
        animation: "fadeUp 0.5s ease both",
        animationDelay: `${delay}ms`,
    }}>
        <Box sx={{
            minWidth: 32, height: 32, borderRadius: "50%",
            background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
            color: "#fff", fontWeight: 700, fontSize: 13,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 3px 10px rgba(21,101,192,0.35)",
            flexShrink: 0, mt: "2px",
        }}>
            {num}
        </Box>
        <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.7, pt: "6px" }}>
            {text}
        </Typography>
    </Box>
);

/* ── Stat card ── */
const StatCard = ({ icon, label, value, accent, delay }) => (
    <Paper elevation={0} sx={{
        p: 3, borderRadius: 3,
        border: "1px solid",
        borderColor: accent ? "primary.main" : "#e8edf5",
        background: accent
            ? "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)"
            : "#fff",
        display: "flex", alignItems: "center", gap: 2.5,
        animation: "fadeUp 0.5s ease both",
        animationDelay: `${delay}ms`,
        transition: "box-shadow 0.25s, transform 0.25s",
        "&:hover": {
            boxShadow: accent
                ? "0 8px 28px rgba(21,101,192,0.35)"
                : "0 6px 20px rgba(0,0,0,0.08)",
            transform: "translateY(-2px)",
        },
    }}>
        <Box sx={{
            width: 48, height: 48, borderRadius: 2.5,
            background: accent ? "rgba(255,255,255,0.18)" : "rgba(21,101,192,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent ? "#fff" : "#1565c0",
            fontSize: 24,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: accent ? "#fff" : "#1a2236", lineHeight: 1 }}>
                <AnimatedCounter target={typeof value === "number" ? value : 0} />
                {typeof value === "string" && value}
            </Typography>
            <Typography variant="caption" sx={{ color: accent ? "rgba(255,255,255,0.8)" : "#7a8aab", fontWeight: 500 }}>
                {label}
            </Typography>
        </Box>
    </Paper>
);

/* ── Main Component ── */
const ReferralDashboard = () => {
    const { user_refferal_code } = useSelector((state) => state.user);
    const [referrals, setReferrals] = useState([]);
    const [total, setTotal] = useState(0);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const getData = async () => {
            try {
                const res = await investorApi.getReferrals();
                setReferrals(res.data.users);
                setTotal(res.data.total);
            } catch (err) {
                console.log("Referral error", err);
            }
        };
        getData();
    }, []);

    const referralLink = `https://jemins.in/register?ref=${user_refferal_code}`;

    const copyLink = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
    };

    const steps = [
        "Share a property link from the platform with your friends or family.",
        "When they open the property and register using your shared link, they will be added to your referral list.",
        "If they purchase a property, your referral reward will be credited to your wallet.",
        "You can request a withdrawal from your wallet anytime.",
        "Admin will review your request and transfer the amount to your bank account.",
    ];

    return (
        <>
            <style>{`
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .ref-row:hover td { background: #f0f6ff !important; }
            `}</style>

            <Box sx={{ background: "#f5f7fb", minHeight: "100vh", pb: 6 }}>

                {/* ── Page Header Banner ── */}
                <Box sx={{
                    background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
                    px: { xs: 2, md: 5 }, pt: 5, pb: 8,
                    position: "relative", overflow: "hidden",
                }}>
                    <Box sx={{ position: "absolute", top: -40, right: -40, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -70, left: "28%", width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", top: 20, left: -60, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

                    <Box sx={{ position: "relative", zIndex: 1, maxWidth: 900, mx: "auto" }}>
                        <Chip
                            label="✦  Referral Program"
                            size="small"
                            sx={{ background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, letterSpacing: "0.04em", mb: 2, fontSize: 11 }}
                        />
                        <Typography variant="h4" fontWeight={700} sx={{ color: "#fff", mb: 1 }}>
                            Share &amp; Earn Money
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", maxWidth: 500 }}>
                            Share your unique referral link.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 3 }, mt: -4 }}>

                    {/* ── Stat Cards (overlap the banner) ── */}
                    <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
                        <Grid item xs={12} >
                            <StatCard
                                icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
                                label="Total Referrals"
                                value={total}
                                // accent
                                delay={0}
                            />
                        </Grid>

                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid #e8edf5",
                            flex: 1
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} color="#1a2236" mb={0.5}>
                            How It Works
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#7a8aab", display: "block", mb: 3 }}>
                            5 simple steps to earn rewards
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            {steps.map((s, i) => (
                                <StepItem key={i} num={i + 1} text={s} delay={i * 70 + 300} />
                            ))}
                        </Box>
                    </Paper>



                    {/* ── Referrals Table ── */}

                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            border: "1px solid #e8edf5",
                            overflow: "hidden",
                            flex: 1,
                            mt: 3
                        }}
                    >
                        <Box sx={{
                            px: 3, py: 2.5,
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            borderBottom: "1px solid #f0f4fa",
                        }}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} color="#1a2236">
                                    Registered via Your Link
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#7a8aab" }}>
                                    {total} {total === 1 ? "person" : "people"} joined through your link
                                </Typography>
                            </Box>
                            <Chip
                                label={`${total} Total`}
                                size="small"
                                sx={{ background: "#e8f0fe", color: "#1565c0", fontWeight: 600, fontSize: 12 }}
                            />
                        </Box>

                        {referrals.length === 0 ? (
                            <Box sx={{ py: 9, textAlign: "center" }}>
                                <PeopleAltOutlinedIcon sx={{ fontSize: 52, color: "#d0daea", mb: 1.5 }} />
                                <Typography variant="body2" color="#9aabb8" fontWeight={500}>
                                    No referrals yet
                                </Typography>
                                <Typography variant="caption" color="#b0bec5">
                                    Share your link to start earning rewards
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ background: "#f8fafc" }}>
                                            {["#", "ID", "Name", "Joined Date"].map((h) => (
                                                <TableCell key={h} sx={{
                                                    fontSize: 11, fontWeight: 700, color: "#7a8aab",
                                                    textTransform: "uppercase", letterSpacing: "0.07em",
                                                    py: 1.5, borderBottom: "1px solid #e8edf5",
                                                }}>
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {referrals.map((r, index) => (
                                            <TableRow
                                                key={index}
                                                className="ref-row"
                                                sx={{
                                                    animation: `fadeUp 0.4s ease ${index * 50 + 400}ms both`,
                                                    "& td": {
                                                        transition: "background 0.15s",
                                                        borderBottom: "1px solid #f0f4fa",
                                                    },
                                                }}
                                            >
                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Box sx={{
                                                        width: 26, height: 26, borderRadius: "50%",
                                                        background: "#e8f0fe", color: "#1565c0",
                                                        fontSize: 12, fontWeight: 700,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5, fontSize: 13, color: "#4a5568" }}>
                                                    {r.refferal_code}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Avatar sx={{
                                                            width: 32, height: 32, fontSize: 13, fontWeight: 600,
                                                            background: `hsl(${(index * 53 + 200) % 360}, 50%, 88%)`,
                                                            color: `hsl(${(index * 53 + 200) % 360}, 55%, 32%)`,
                                                        }}>
                                                            {r.FirstName?.charAt(0)?.toUpperCase()}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight={600} color="#1a2236">
                                                            {r.FirstName}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Chip
                                                        label={new Date(r.CreatedOn).toLocaleDateString("en-IN", {
                                                            day: "2-digit", month: "short", year: "numeric",
                                                        })}
                                                        size="small"
                                                        sx={{ background: "#f0f4fa", color: "#4a5568", fontSize: 11, fontWeight: 500 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>



                </Box>
            </Box>
        </>
    );
};

export default ReferralDashboard;
